// Libraries
import React from 'react';
import PropTypes from 'prop-types';
import {
    LineChart,
    AreaChart,
    BarChart,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    CartesianAxis,
    Bar,
    PieChart,
    Pie,
    Area,
    ResponsiveContainer,
    Tooltip,
    Cell,
} from 'recharts';

// Helpers
import { capitaliseString } from '../../utils/helperMethods';

// Styles
import './charts.scss';

/**
 * Charts Component
 *
 * @param {string} customClass - React component
 */

const formatLabel = (key, cardNature) => {
    if (key && key.indexOf('values') === -1) {
        return capitaliseString(key);
    }
    const label = key.split('.');
    const newLabel = label && label.length && label[label.length - 1];
    const removeExtra = newLabel.split('_').join(' ');
    if (cardNature && cardNature[removeExtra]) {
        return cardNature[removeExtra].codeNameUI;
    }
    return capitaliseString(removeExtra);
};

const CustomTooltip = ({ active, payload, label, staticPayValues }) => {
    if (active) {
        return (
            <div className="custom-tooltip">
                <p
                    className="tooltip-title"
                    style={{
                        fontWeight:
                            payload &&
                            payload[0] &&
                            payload[0].unit ===
                                'values.FAILED_TRANSACTION_COUNT'
                                ? 'normal'
                                : '',
                    }}
                >
                    {label}
                </p>
                <ul>
                    {payload &&
                        payload.map(item => (
                            <li
                                style={{
                                    color:
                                        item.unit ===
                                        'values.FAILED_TRANSACTION_COUNT'
                                            ? '#000'
                                            : item.stroke,
                                }}
                            >
                                <label>
                                    {formatLabel(
                                        item.unit,
                                        staticPayValues
                                            ? staticPayValues.CARD_NATURE
                                            : null
                                    )}{' '}
                                    :
                                </label>
                                <span>{item.value}</span>
                            </li>
                        ))}
                </ul>
            </div>
        );
    }

    return null;
};

const getColor = (legends, item) => {
    if (legends && legends[item]) {
        return legends[item].color;
    }
    return '#000';
};
const getAllKeys = values => {
    let keys = [];
    values.forEach(item => {
        const values = Object.keys(item.values);
        keys = [...keys, ...values];
    });
    const uniqueSet = new Set(keys);
    return [...uniqueSet];
};

const getChartKeys = (values, legends) => {
    if (values && values.length) {
        const allKeys = getAllKeys(values);
        const lineValue = allKeys.sort().map(item => {
            return {
                key: item,
                stroke: getColor(legends, item),
                type: 'monotone',
            };
        });
        return lineValue;
    }
    return [];
};

const LineGraph = props => {
    const { data, legends, staticPayValues } = props;
    const lineData = getChartKeys(data, legends);
    return (
        <ResponsiveContainer width="100%" height={250}>
            <LineChart
                data={data}
                margin={{
                    top: 10,
                    right: 30,
                    left: 0,
                    bottom: 0,
                }}
            >
                <Tooltip
                    content={
                        <CustomTooltip staticPayValues={staticPayValues} />
                    }
                />
                <XAxis
                    dataKey="aggregator"
                    tick={{ fontSize: 12 }}
                    tickLine={{ stroke: '#d8d8d8' }}
                    axisLine={{ stroke: '#d8d8d8' }}
                    tickMargin={7}
                />
                <YAxis
                    padding={{ top: 40 }}
                    tick={{ fontSize: 12 }}
                    tickLine={{ stroke: '#d8d8d8' }}
                    axisLine={{ stroke: '#d8d8d8' }}
                    tickMargin={7}
                />
                <CartesianGrid stroke="#d8d8d8" />
                {lineData &&
                    lineData.length &&
                    lineData.map(line => (
                        <Line
                            type={line.type}
                            dataKey={data =>
                                parseInt(data.values[line.key], 10)
                            }
                            unit={`values.${line.key}`}
                            stroke={line.stroke}
                            strokeWidth={2}
                            isAnimationActive={false}
                        />
                    ))}
            </LineChart>
        </ResponsiveContainer>
    );
};
const BarGraph = props => {
    const { data, legends, staticPayValues } = props;
    const barData = getChartKeys(data, legends);
    return (
        <ResponsiveContainer width="100%" height={250}>
            <BarChart
                data={data}
                barGap={5}
                margin={{
                    top: 10,
                    right: 30,
                    left: 0,
                    bottom: 0,
                }}
            >
                <Tooltip
                    content={
                        <CustomTooltip staticPayValues={staticPayValues} />
                    }
                    cursor={{ fill: '#f6f6f6' }}
                />
                <XAxis
                    dataKey="aggregator"
                    tick={{ fontSize: 12 }}
                    tickLine={{ stroke: '#d8d8d8' }}
                    axisLine={{ stroke: '#d8d8d8' }}
                    tickMargin={7}
                />
                <YAxis
                    padding={{ top: 40 }}
                    tick={{ fontSize: 12 }}
                    tickLine={{ stroke: '#d8d8d8' }}
                    axisLine={{ stroke: '#d8d8d8' }}
                    tickMargin={7}
                />
                <CartesianGrid stroke="#d8d8d8" />
                {barData &&
                    barData.length &&
                    barData.map(line => (
                        <Bar
                            maxBarSize={15}
                            type={line.type}
                            dataKey={data =>
                                parseInt(data.values[line.key], 10)
                            }
                            unit={`values.${line.key}`}
                            stroke={line.stroke}
                            fill={line.stroke}
                        />
                    ))}
            </BarChart>
        </ResponsiveContainer>
    );
};

const AreaGraph = props => {
    const { data, legends, staticPayValues } = props;
    const areaData = getChartKeys(data, legends);
    return (
        <ResponsiveContainer width="100%" height={250}>
            <AreaChart
                data={data}
                margin={{
                    top: 10,
                    right: 30,
                    left: 0,
                    bottom: 0,
                }}
            >
                <Tooltip
                    content={
                        <CustomTooltip staticPayValues={staticPayValues} />
                    }
                />
                <CartesianGrid stroke="#d8d8d8" />
                <XAxis
                    dataKey="aggregator"
                    tick={{ fontSize: 12 }}
                    tickLine={{ stroke: '#d8d8d8' }}
                    axisLine={{ stroke: '#d8d8d8' }}
                    tickMargin={7}
                />
                <YAxis
                    padding={{ top: 40 }}
                    tick={{ fontSize: 12 }}
                    tickLine={{ stroke: '#d8d8d8' }}
                    axisLine={{ stroke: '#d8d8d8' }}
                    tickMargin={7}
                />
                {areaData &&
                    areaData.length &&
                    areaData.map(line => (
                        <Area
                            maxBarSize={30}
                            stackId="1"
                            fillOpacity={1}
                            type={line.type}
                            dataKey={`values.${line.key}`}
                            unit={`values.${line.key}`}
                            stroke={line.stroke}
                            fill={line.stroke}
                        />
                    ))}
            </AreaChart>
        </ResponsiveContainer>
    );
};

const PieGraph = props => {
    const { data, cx, cy } = props;
    return (
        <ResponsiveContainer width="100%" height={300}>
            <PieChart>
                {/* <Tooltip /> */}
                <Pie
                    nameKey="aggregator"
                    dataKey="value"
                    isAnimationActive={false}
                    animationBegin={0}
                    animationDuration={700}
                    data={data}
                    // cx={cx}
                    // cy={cy}
                    innerRadius={30}
                    outerRadius={100}
                    fill="#8884d8"
                    labelLine={{ stroke: '#999' }}
                    label={({
                        cx,
                        cy,
                        midAngle,
                        innerRadius,
                        outerRadius,
                        value,
                        index,
                    }) => {
                        const RADIAN = Math.PI / 180;
                        // eslint-disable-next-line
                        const radius =
                            25 + innerRadius + (outerRadius - innerRadius);
                        // // eslint-disable-next-line
                        // const x = cx + radius * Math.cos(-midAngle * RADIAN);
                        // // eslint-disable-next-line
                        // const y = cy + radius * Math.sin(-midAngle * RADIAN);

                        const sin = Math.sin(-RADIAN * midAngle);
                        const cos = Math.cos(-RADIAN * midAngle);
                        const sx = cx + (outerRadius + 10) * cos;
                        const sy = cy + (outerRadius + 10) * sin;
                        const mx = cx + (outerRadius + 30) * cos;
                        const my = cy + (outerRadius + 30) * sin;
                        const ex = mx + (cos >= 0 ? 1 : -1) * 22;
                        const ey = my;
                        const textAnchor = cos >= 0 ? 'start' : 'end';

                        return (
                            <g>
                                <path
                                    d={`M${sx},${sy}L${mx},${my}L${ex},${ey}`}
                                    stroke={'#999'}
                                    fill="none"
                                />
                                <circle
                                    cx={ex}
                                    cy={ey}
                                    r={2}
                                    fill="#999"
                                    stroke="none"
                                />
                                <text
                                    x={ex + (cos >= 0 ? 1 : -1) * 12}
                                    y={ey}
                                    textAnchor={textAnchor}
                                    fill="#333"
                                    fontSize={12}
                                >
                                    {data[index].aggregator}
                                </text>
                                <text
                                    x={ex + (cos >= 0 ? 1 : -1) * 12}
                                    y={ey}
                                    dy={18}
                                    textAnchor={textAnchor}
                                    fill="#333"
                                    fontSize={14}
                                    fontWeight="bold"
                                >
                                    {data[index].TRANSACTION_COUNT}
                                </text>
                                {/* <text
                                    x={x}
                                    y={y}
                                    // fill={data[index].color}
                                    fill="#000"
                                    textAnchor={x > cx ? 'start' : 'end'}
                                    dominantBaseline="central"
                                >
                                    {data[index].aggregator}
                                </text> */}
                            </g>
                        );
                    }}
                >
                    {data && data.map(entry => <Cell fill={entry.color} />)}
                </Pie>
            </PieChart>
        </ResponsiveContainer>
    );
};

PieGraph.defaultProps = {
    cx: 200,
    cy: 100,
};

const renderChart = ({ type, ...props }) => {
    if (type === 'line') {
        return <LineGraph {...props} type={type} />;
    } else if (type === 'bar') {
        return <BarGraph {...props} type={type} />;
    } else if (type === 'area') {
        return <AreaGraph {...props} type={type} />;
    } else if (type === 'pie') {
        return <PieGraph {...props} type={type} />;
    }
};
const Charts = ({ customClass, ...rest }) => {
    return <div className="chartWrap">{renderChart(rest)}</div>;
};

Charts.propTypes = {
    customClass: PropTypes.string,
    type: PropTypes.string,
    data: PropTypes.object,
};

LineGraph.propTypes = {
    data: PropTypes.object,
    legends: PropTypes.array,
};

BarGraph.propTypes = {
    data: PropTypes.object,
    legends: PropTypes.object,
};

AreaGraph.propTypes = {
    data: PropTypes.object,
    legends: PropTypes.object,
};
PieGraph.propTypes = {
    data: PropTypes.object,
    legends: PropTypes.object,
    cx: PropTypes.number,
    cy: PropTypes.number,
};

CustomTooltip.propTypes = {
    active: PropTypes.bool,
    payload: PropTypes.object,
    label: PropTypes.string,
};

export default Charts;
